import React, { useState } from 'react';
import { 
  Container,
  Paper,
  Typography,
  TextField, 
  Button, 
  Box, 
  MenuItem, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select,
  FormHelperText // ✅ Add this import
} from '@mui/material';
import { addCustomer } from '../services/customerService';
import { useNavigate } from 'react-router-dom';
import { countries } from '../../../data/countries';
import { useTranslation } from "react-i18next";

const AddCustomerPage = () => {
  const [VATNumber, setVATNumber] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [street, setStreet] = useState('');
  const [box, setBox] = useState('');
  const [postCode, setPostCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Belgium');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [isActive, setIsActive] = useState('true');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // ✅ Clear previous errors first
    setErrors({});
    
    try {
      const record = {
        VATNumber,
        Name: name,
        Email: email,
        Street: street,
        Box: box,
        PostCode: postCode,
        City: city,
        Country: country,
        PhoneNumber: phoneNumber,
        MobilePhone: mobilePhone,
        IsActive: isActive === 'true',
        CreatedDate: new Date().toISOString(),
        IsDeleted: false
      };

      // ✅ Validate all fields at once
      const newErrors = {};
      
      if (!record.VATNumber || record.VATNumber.trim() === '') {
        newErrors.VATNumber = "This field is required";
      }
      
      if (!record.Name || record.Name.trim() === '') {
        newErrors.Name = "This field is required";
      }
      
      if (!record.Email || record.Email.trim() === '') {
        newErrors.Email = "This field is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(record.Email)) {
        newErrors.Email = "Invalid email format";
      }
      
      if (!record.PhoneNumber || record.PhoneNumber.trim() === '') {
        newErrors.PhoneNumber = "This field is required";
      }
      
      if (!record.MobilePhone || record.MobilePhone.trim() === '') {
        newErrors.MobilePhone = "This field is required";
      }
      
      if (!record.Street || record.Street.trim() === '') {
        newErrors.Street = "This field is required";
      }
      
      if (!record.PostCode || record.PostCode.trim() === '') {
        newErrors.PostCode = "This field is required";
      }
      
      if (!record.City || record.City.trim() === '') {
        newErrors.City = "This field is required";
      }
      
      if (!record.Country) {
        newErrors.Country = "This field is required";
      }

      // ✅ If there are errors, set them and STOP
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setLoading(false);
        
        // ✅ Scroll to the first error field
        setTimeout(() => {
          const firstErrorField = Object.keys(newErrors)[0];
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }, 100);
        
        return;
      }

      // ✅ Only reach here if no errors
      await addCustomer(record);
      navigate('/customers');
      
    } catch (error) {
      console.error('Error adding customer:', error);
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/customers');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h5" 
          component="h1" 
          sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            textAlign: 'left', 
            mb: 0.5 
          }}
        >
          {t("add_customer")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("add_customer_desc")}
        </Typography>
      </Box>

      <Paper 
        elevation={2} 
        sx={{ 
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t("basic_information")}
                </Typography>
              </Box>
            </Grid>

            {/* VAT Number */}
            <Grid item xs={12} md={6}>
              <TextField
                name="VATNumber"
                label={t("vat_number") + " *"} // ✅ Add * to show required
                value={VATNumber}
                onChange={(e) => {
                  setVATNumber(e.target.value);
                  if (errors.VATNumber) {
                    setErrors(prev => ({ ...prev, VATNumber: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.VATNumber)}
                helperText={errors.VATNumber || ' '}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': { // ✅ Keep label normal color
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)' // Normal label color, not red
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f' // Error text stays red
                    }
                  }
                }}
              />
            </Grid>

            {/* Customer Name */}
            <Grid item xs={12} md={6}>
              <TextField
                name="Name"
                label={t("customer_name") + " *"}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.Name) {
                    setErrors(prev => ({ ...prev, Name: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.Name)}
                helperText={errors.Name || ' '}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <TextField
                name="Email"
                label={t("email_address") + " *"}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.Email) {
                    setErrors(prev => ({ ...prev, Email: undefined }));
                  }
                }}
                fullWidth
                type="email"
                error={Boolean(errors.Email)}
                helperText={errors.Email || ' '}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Phone Number */}
            <Grid item xs={12} md={6}>
              <TextField
                name="PhoneNumber"
                label={t("phone_number") + " *"}
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  if (errors.PhoneNumber) {
                    setErrors(prev => ({ ...prev, PhoneNumber: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.PhoneNumber)}
                helperText={errors.PhoneNumber || ' '}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Mobile Phone */}
            <Grid item xs={12} md={6}>
              <TextField
                name="MobilePhone"
                label={t("mobile_phone") + " *"}
                value={mobilePhone}
                onChange={(e) => {
                  setMobilePhone(e.target.value);
                  if (errors.MobilePhone) {
                    setErrors(prev => ({ ...prev, MobilePhone: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.MobilePhone)}
                helperText={errors.MobilePhone || ' '}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                disabled={loading}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              >
                <InputLabel>{t("status")}</InputLabel>
                <Select
                  value={isActive}
                  onChange={e => setIsActive(e.target.value)}
                  label={t("status")}
                >
                  <MenuItem value="true">{t("active")}</MenuItem>
                  <MenuItem value="false">{t("inactive")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t("address_information")}
                </Typography>
              </Box>
            </Grid>

            {/* Street Address */}
            <Grid item xs={12}>
              <TextField
                name="Street"
                label={t("street_address") + " *"}
                value={street}
                onChange={(e) => {
                  setStreet(e.target.value);
                  if (errors.Street) {
                    setErrors(prev => ({ ...prev, Street: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.Street)}
                helperText={errors.Street || ' '}
                disabled={loading}
                multiline
                rows={2}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Box */}
            <Grid item xs={12} md={4}>
              <TextField
                name="Box"
                label={t("box_unit")}
                value={box}
                onChange={e => setBox(e.target.value)}
                fullWidth
                disabled={loading}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                helperText=" "
              />
            </Grid>

            {/* Post Code */}
            <Grid item xs={12} md={4}>
              <TextField
                name="PostCode"
                label={t("postal_code") + " *"}
                value={postCode}
                onChange={(e) => {
                  setPostCode(e.target.value);
                  if (errors.PostCode) {
                    setErrors(prev => ({ ...prev, PostCode: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.PostCode)}
                helperText={errors.PostCode || ' '}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} md={4}>
              <TextField
                name="City"
                label={t("city") + " *"}
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  if (errors.City) {
                    setErrors(prev => ({ ...prev, City: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.City)}
                helperText={errors.City || ' '}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={Boolean(errors.Country)}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  }
                }}
              >
                <InputLabel>{t("country")} *</InputLabel>
                <Select
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    if (errors.Country) {
                      setErrors(prev => ({ ...prev, Country: undefined }));
                    }
                  }}
                  label={t("country") + " *"}
                >
                  {countries.map((c) => (
                    <MenuItem key={c.code} value={c.name}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ marginLeft: 0.5, fontSize: '0.75rem' }}>
                  {errors.Country || ' '}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end', 
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 2
                  }}
                >
                  {loading ? t("creating") : t("create_customer")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AddCustomerPage;